import { useState, useEffect } from 'react';
import { api } from '../api';

export default function CreateInvoiceModal({ presentation, prebuiltLineItems, presentationId, clientId, clientName, clientEmail, onClose, onCreated, getToken, showToast }) {
  // Starts blank rather than a hardcoded 'INV-001' — the effect below fills
  // in the server's suggested next number for THIS distributor as soon as it
  // loads. Still just a starting point: the field stays fully editable, and
  // the real uniqueness guarantee is the duplicate check on save.
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [numberLoading, setNumberLoading] = useState(true);
  const [dueDate, setDueDate] = useState('');
  const [taxLabel, setTaxLabel] = useState('');
  const [taxRate, setTaxRate] = useState(0);
  const [notes, setNotes] = useState('');
  const [lineItems, setLineItems] = useState([]);
  const [taxAmount, setTaxAmount] = useState(0);
  const [subtotal, setSubtotal] = useState(0);
  const [total, setTotal] = useState(0);
  const [saving, setSaving] = useState(false);
  const [editingItemIdx, setEditingItemIdx] = useState(null);
  const [validationError, setValidationError] = useState(null);
  // Ship-to for the invoice. Prefilled from the client's default saved
  // address + profile (same source as the PO flow), editable here, and sent
  // with the create request. The server falls back to the same lookup if
  // this is left entirely blank, so an untouched form still gets an address.
  const EMPTY_SHIP = { name: '', company: '', address1: '', address2: '', city: '', state: '', zip: '', phone: '' };
  const [shipTo, setShipTo] = useState(EMPTY_SHIP);
  const [shipToLoaded, setShipToLoaded] = useState(false);
  const setShip = (k, v) => setShipTo(s => ({ ...s, [k]: v }));

  useEffect(() => {
    const cid = clientId || presentation?.client_id;
    if (!cid) { setShipToLoaded(true); return; }
    let cancelled = false;
    (async () => {
      try {
        const token = await getToken();
        const [cres, addrs] = await Promise.all([
          api.withToken(token).getClient(cid).catch(() => null),
          api.withToken(token).listClientAddresses(cid).catch(() => []),
        ]);
        if (cancelled) return;
        const c = cres?.client || {};
        const list = Array.isArray(addrs) ? addrs : (addrs?.addresses || []);
        const a = list.find(x => x.is_default) || list[0] || {};
        setShipTo({
          name:     a.ship_to_contact_name || c.contact_name || clientName || presentation?.client_name || c.name || '',
          company:  c.company_name || '',
          address1: a.line1 || '', address2: a.line2 || '',
          city: a.city || '', state: a.state || '', zip: a.postal_code || '',
          phone:    a.ship_to_phone || c.phone || '',
        });
      } catch { /* leave blank; server will attempt its own default */ }
      finally { if (!cancelled) setShipToLoaded(true); }
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line
  }, []);

  // Suggests the next number for THIS distributor as soon as the modal opens.
  // Never overwrites something the user already typed — only fires once, on
  // mount, while the field is still at its initial blank value.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = await getToken();
        const r = await api.withToken(token).getNextInvoiceNumber();
        if (!cancelled) setInvoiceNumber(prev => prev || r?.number || 'INV-001');
      } catch {
        if (!cancelled) setInvoiceNumber(prev => prev || 'INV-001');
      } finally {
        if (!cancelled) setNumberLoading(false);
      }
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line
  }, []);

  useEffect(() => {
    // Preferred path: the parent (Presentations) hands us line items it has
    // ALREADY priced for the client. Use them directly — don't re-derive.
    if (Array.isArray(prebuiltLineItems) && prebuiltLineItems.length > 0) {
      setLineItems(prebuiltLineItems.map((it, i) => {
        const qty = Number(it.qty) > 0 ? Number(it.qty) : 1;
        const price = Number(it.unit_price) >= 0 ? Number(it.unit_price) : 0;
        return {
          id: it.id || `item-${i}`,
          description: it.description || 'Item',
          qty,
          unit_price: price,
          subtotal: qty * price,
          taxable: it.taxable !== false,
        };
      }));
      return;
    }
    // Fallback: derive from a raw presentation.products array if that's all we
    // were given (kept for any caller that doesn't prebuild).
    if (presentation?.products && Array.isArray(presentation.products)) {
      try {
        const items = presentation.products.map((p, i) => {
          const qty = p.quantity && !isNaN(p.quantity) ? Math.max(0, p.quantity) : 1;
          const price = p.price && !isNaN(p.price) ? Math.max(0, p.price) : 0;
          return {
            id: p.id || `item-${i}`,
            description: p.name || 'Unnamed item',
            qty,
            unit_price: price,
            subtotal: qty * price,
            taxable: true,
          };
        });
        setLineItems(items);
      } catch (err) {
        console.error('Failed to extract line items:', err);
        showToast('Failed to load presentation items', 'error');
        onClose();
      }
    }
  }, [presentation, prebuiltLineItems]);

  // Recalculate totals whenever line items or tax rate changes
  useEffect(() => {
    const newSubtotal = lineItems.reduce((sum, item) => {
      if (typeof item.subtotal !== 'number' || !isFinite(item.subtotal)) return sum;
      return sum + item.subtotal;
    }, 0);

    const taxableAmount = lineItems
      .filter((item) => item.taxable)
      .reduce((sum, item) => {
        if (typeof item.subtotal !== 'number' || !isFinite(item.subtotal)) return sum;
        return sum + item.subtotal;
      }, 0);

    const rate = parseFloat(taxRate);
    const validRate = !isNaN(rate) && isFinite(rate) ? Math.min(100, Math.max(0, rate)) : 0;
    const newTaxAmount = parseFloat((taxableAmount * validRate / 100).toFixed(2));
    const newTotal = parseFloat((newSubtotal + newTaxAmount).toFixed(2));

    setSubtotal(newSubtotal);
    setTaxAmount(newTaxAmount);
    setTotal(newTotal);
  }, [lineItems, taxRate]);

  const handleTaxLabelChange = (e) => {
    const val = e.target.value;
    setTaxLabel(val);

    // Try to extract rate from label (e.g., "Sales Tax 9.5%" → 9.5)
    const match = val.match(/(\d+\.?\d*)\s*%/);
    if (match) {
      const rate = parseFloat(match[1]);
      if (!isNaN(rate) && isFinite(rate)) {
        setTaxRate(rate);
      }
    }
  };

  const toggleItemTaxable = (idx) => {
    const updated = [...lineItems];
    updated[idx].taxable = !updated[idx].taxable;
    setLineItems(updated);
  };

  const handleEditItem = (idx) => {
    setEditingItemIdx(idx);
  };

  const handleSaveItem = (idx, updatedItem) => {
    // Validate updated item
    const qty = parseFloat(updatedItem.qty);
    const price = parseFloat(updatedItem.unit_price);

    if (isNaN(qty) || !isFinite(qty) || qty < 0) {
      showToast('Quantity must be a valid number', 'error');
      return;
    }

    if (isNaN(price) || !isFinite(price) || price < 0) {
      showToast('Price must be a valid number', 'error');
      return;
    }

    const updated = [...lineItems];
    updated[idx] = {
      ...updated[idx],
      ...updatedItem,
      qty,
      unit_price: price,
      subtotal: parseFloat((qty * price).toFixed(2)),
    };
    setLineItems(updated);
    setEditingItemIdx(null);
  };

  const removeItem = (idx) => {
    setLineItems(lineItems.filter((_, i) => i !== idx));
  };

  const handleCreateInvoice = async () => {
    setValidationError(null);

    // Validation
    if (!invoiceNumber.trim()) {
      setValidationError('Invoice number is required');
      return;
    }

    if (lineItems.length === 0) {
      setValidationError('Add at least one line item');
      return;
    }

    // Validate all line items have valid amounts
    for (const item of lineItems) {
      if (!isFinite(item.qty) || !isFinite(item.unit_price) || !isFinite(item.subtotal)) {
        setValidationError('All line items must have valid quantities and prices');
        return;
      }
    }

    try {
      setSaving(true);
      const token = await getToken();

      const payload = {
        presentationId: presentationId || presentation?.id,
        invoiceNumber: invoiceNumber.trim(),
        lineItems,
        taxLabel: taxLabel.trim(),
        taxRate: parseFloat(taxRate) || 0,
        taxAmount: parseFloat(taxAmount.toFixed(2)),
        subtotal: parseFloat(subtotal.toFixed(2)),
        total: parseFloat(total.toFixed(2)),
        notes: notes.trim(),
        dueDate: dueDate || null,
        clientId: clientId || presentation?.client_id || null,
        clientName: clientName || presentation?.client_name || null,
        clientEmail: clientEmail || presentation?.client_email || null,
        shipTo: Object.values(shipTo).some(v => String(v || '').trim()) ? shipTo : null,
      };

      const result = await api.withToken(token).createInvoice(payload);
      if (result.ok && result.invoice) {
        showToast('Invoice created successfully');
        if (onCreated) onCreated(result.invoice);
        onClose();
      } else {
        setValidationError(result.error || 'Failed to create invoice');
      }
    } catch (err) {
      console.error('Failed to create invoice:', err);
      setValidationError(err.message || 'Failed to create invoice');
    } finally {
      setSaving(false);
    }
  };

  const formatCurrency = (n) => {
    try {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n || 0);
    } catch {
      return '$' + (n || 0).toFixed(2);
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 999,
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        style={{
          background: '#FFF',
          borderRadius: '12px',
          maxWidth: '700px',
          maxHeight: '90vh',
          overflow: 'auto',
          width: '90%',
          padding: '32px',
          boxShadow: '0 20px 25px rgba(0,0,0,0.15)',
        }}
      >
        <div style={{ marginBottom: '24px' }}>
          <h2 style={{ margin: '0 0 8px 0', fontSize: 24, fontWeight: 600 }}>Create Invoice</h2>
          <p style={{ margin: 0, color: '#666', fontSize: 14 }}>From: {presentation?.name || 'Unnamed presentation'}</p>
        </div>

        {validationError && (
          <div style={{ marginBottom: '16px', padding: '12px', background: '#FEE2E2', color: '#DC2626', borderRadius: '6px', fontSize: 14 }}>
            {validationError}
          </div>
        )}

        {/* Invoice details */}
        <div style={{ marginBottom: '24px', paddingBottom: '24px', borderBottom: '1px solid #E5E7EB' }}>
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: '6px', color: '#333' }}>
              Invoice Number *
            </label>
            <input
              type="text"
              value={invoiceNumber}
              placeholder={numberLoading ? 'Loading suggested number…' : 'INV-001'}
              onChange={(e) => setInvoiceNumber(e.target.value)}
              style={{ width: '100%', padding: '8px 12px', border: '1px solid #D1D5DB', borderRadius: '6px', fontSize: 14 }}
            />
            <div style={{ fontSize: 11, color: '#94A3B8', marginTop: 4 }}>
              Suggested from your last invoice — edit freely, it just has to be unique.
            </div>
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: '6px', color: '#333' }}>
              Due Date
            </label>
            <input
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              style={{ width: '100%', padding: '8px 12px', border: '1px solid #D1D5DB', borderRadius: '6px', fontSize: 14 }}
            />
          </div>
        </div>

        {/* Line items */}
        <div style={{ marginBottom: '24px', paddingBottom: '24px', borderBottom: '1px solid #E5E7EB' }}>
          <div style={{ fontSize: 14, fontWeight: 600, marginBottom: '12px' }}>Line Items *</div>

          {lineItems.map((item, idx) => (
            <div key={item.id || idx} style={{ marginBottom: '12px', padding: '12px', background: '#F9FAFB', borderRadius: '6px' }}>
              {editingItemIdx === idx ? (
                <EditLineItemForm
                  item={item}
                  onSave={(updated) => handleSaveItem(idx, updated)}
                  onCancel={() => setEditingItemIdx(null)}
                />
              ) : (
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '8px' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 14, fontWeight: 500 }}>{item.description}</div>
                      <div style={{ fontSize: 12, color: '#666' }}>
                        {item.qty} × {formatCurrency(item.unit_price)} = {formatCurrency(item.subtotal)}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '6px' }}>
                      <button
                        onClick={() => handleEditItem(idx)}
                        style={{
                          fontSize: 11,
                          padding: '4px 8px',
                          border: '1px solid #D1D5DB',
                          background: '#FFF',
                          borderRadius: '4px',
                          cursor: 'pointer',
                        }}
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => removeItem(idx)}
                        style={{
                          fontSize: 11,
                          padding: '4px 8px',
                          border: '1px solid #FCA5A5',
                          background: '#FEE2E2',
                          color: '#DC2626',
                          borderRadius: '4px',
                          cursor: 'pointer',
                        }}
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={item.taxable}
                      onChange={() => toggleItemTaxable(idx)}
                      style={{ cursor: 'pointer' }}
                    />
                    <span style={{ fontSize: 12, color: '#666' }}>Taxable</span>
                  </label>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Tax */}
        <div style={{ marginBottom: '24px', paddingBottom: '24px', borderBottom: '1px solid #E5E7EB' }}>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: '6px', color: '#333' }}>
            Tax Label & Rate
          </label>
          <input
            type="text"
            value={taxLabel}
            onChange={handleTaxLabelChange}
            placeholder="e.g., Seattle Sales Tax 10.25%"
            style={{ width: '100%', padding: '8px 12px', border: '1px solid #D1D5DB', borderRadius: '6px', fontSize: 14, marginBottom: '6px' }}
          />
          <div style={{ fontSize: 12, color: '#666' }}>
            Tax rate: {parseFloat(taxRate).toFixed(2)}% • Amount: {formatCurrency(taxAmount)}
          </div>
        </div>

        {/* Ship to */}
        <div style={{ marginBottom: '24px', paddingBottom: '24px', borderBottom: '1px solid #E5E7EB' }}>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: '2px', color: '#333' }}>
            Ship to
          </label>
          <div style={{ fontSize: 11, color: '#94A3B8', marginBottom: '10px' }}>
            {shipToLoaded ? "Prefilled from the client's saved address — edit anything before creating." : 'Loading saved address…'}
          </div>
          {(() => {
            const inp = { width: '100%', padding: '8px 12px', border: '1px solid #D1D5DB', borderRadius: '6px', fontSize: 14, fontFamily: 'inherit', boxSizing: 'border-box' };
            const row = { display: 'grid', gap: 8, marginBottom: 8 };
            return (
              <>
                <div style={{ ...row, gridTemplateColumns: '1fr 1fr' }}>
                  <input value={shipTo.name} onChange={e => setShip('name', e.target.value)} placeholder="Contact name" style={inp} />
                  <input value={shipTo.company} onChange={e => setShip('company', e.target.value)} placeholder="Company" style={inp} />
                </div>
                <div style={{ ...row, gridTemplateColumns: '1fr' }}>
                  <input value={shipTo.address1} onChange={e => setShip('address1', e.target.value)} placeholder="Street address" style={inp} />
                </div>
                <div style={{ ...row, gridTemplateColumns: '1fr' }}>
                  <input value={shipTo.address2} onChange={e => setShip('address2', e.target.value)} placeholder="Suite, unit, etc. (optional)" style={inp} />
                </div>
                <div style={{ ...row, gridTemplateColumns: '2fr 1fr 1fr' }}>
                  <input value={shipTo.city} onChange={e => setShip('city', e.target.value)} placeholder="City" style={inp} />
                  <input value={shipTo.state} onChange={e => setShip('state', e.target.value)} placeholder="State" style={inp} />
                  <input value={shipTo.zip} onChange={e => setShip('zip', e.target.value)} placeholder="ZIP" style={inp} />
                </div>
                <div style={{ ...row, gridTemplateColumns: '1fr 1fr', marginBottom: 0 }}>
                  <input value={shipTo.phone} onChange={e => setShip('phone', e.target.value)} placeholder="Phone" style={inp} />
                </div>
              </>
            );
          })()}
        </div>

        {/* Notes */}
        <div style={{ marginBottom: '24px', paddingBottom: '24px', borderBottom: '1px solid #E5E7EB' }}>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: '6px', color: '#333' }}>
            Notes / Payment Terms
          </label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            style={{
              width: '100%',
              padding: '8px 12px',
              border: '1px solid #D1D5DB',
              borderRadius: '6px',
              fontSize: 14,
              fontFamily: 'inherit',
            }}
          />
        </div>

        {/* Totals */}
        <div style={{ marginBottom: '24px', paddingBottom: '24px', borderBottom: '1px solid #E5E7EB' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, marginBottom: '8px' }}>
            <span>Subtotal:</span>
            <span>{formatCurrency(subtotal)}</span>
          </div>
          {taxAmount > 0 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, marginBottom: '8px' }}>
              <span>{taxLabel || 'Tax'}:</span>
              <span>{formatCurrency(taxAmount)}</span>
            </div>
          )}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              fontSize: 18,
              fontWeight: 600,
              paddingTop: '8px',
              borderTop: '1px solid #E5E7EB',
            }}
          >
            <span>Total:</span>
            <span>{formatCurrency(total)}</span>
          </div>
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', gap: '12px' }}>
          <button
            onClick={onClose}
            disabled={saving}
            style={{
              flex: 1,
              padding: '12px',
              border: '1px solid #D1D5DB',
              background: '#FFF',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: 14,
              fontWeight: 500,
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleCreateInvoice}
            disabled={saving}
            style={{
              flex: 1,
              padding: '12px',
              background: '#000',
              color: '#FFF',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: 14,
              fontWeight: 600,
              opacity: saving ? 0.7 : 1,
            }}
          >
            {saving ? 'Creating...' : 'Create Invoice'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Helper component for editing a line item
function EditLineItemForm({ item, onSave, onCancel }) {
  const [desc, setDesc] = useState(item.description);
  const [qty, setQty] = useState(item.qty.toString());
  const [price, setPrice] = useState(item.unit_price.toString());
  const [error, setError] = useState(null);

  const handleSave = () => {
    setError(null);
    const parsedQty = parseFloat(qty);
    const parsedPrice = parseFloat(price);

    if (isNaN(parsedQty) || !isFinite(parsedQty) || parsedQty < 0) {
      setError('Quantity must be a valid number');
      return;
    }

    if (isNaN(parsedPrice) || !isFinite(parsedPrice) || parsedPrice < 0) {
      setError('Price must be a valid number');
      return;
    }

    onSave({ description: desc, qty: parsedQty, unit_price: parsedPrice });
  };

  return (
    <div>
      {error && <div style={{ color: '#DC2626', fontSize: 12, marginBottom: '8px' }}>{error}</div>}
      <input
        type="text"
        value={desc}
        onChange={(e) => setDesc(e.target.value)}
        placeholder="Description"
        style={{ width: '100%', padding: '6px 8px', border: '1px solid #D1D5DB', borderRadius: '4px', fontSize: 13, marginBottom: '6px' }}
      />
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '8px' }}>
        <input
          type="number"
          value={qty}
          onChange={(e) => setQty(e.target.value)}
          placeholder="Qty"
          min="0"
          step="0.01"
          style={{ padding: '6px 8px', border: '1px solid #D1D5DB', borderRadius: '4px', fontSize: 13 }}
        />
        <input
          type="number"
          value={price}
          onChange={(e) => setPrice(e.target.value)}
          placeholder="Price"
          min="0"
          step="0.01"
          style={{ padding: '6px 8px', border: '1px solid #D1D5DB', borderRadius: '4px', fontSize: 13 }}
        />
      </div>
      <div style={{ display: 'flex', gap: '6px' }}>
        <button
          onClick={handleSave}
          style={{
            flex: 1,
            padding: '6px',
            background: '#000',
            color: '#FFF',
            border: 'none',
            borderRadius: '4px',
            fontSize: 12,
            cursor: 'pointer',
            fontWeight: 500,
          }}
        >
          Save
        </button>
        <button
          onClick={onCancel}
          style={{
            flex: 1,
            padding: '6px',
            background: '#E5E7EB',
            border: 'none',
            borderRadius: '4px',
            fontSize: 12,
            cursor: 'pointer',
            fontWeight: 500,
          }}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
